/**
 * An `<svg>` on the page to a file that stands on its own: a standalone SVG
 * (`svgToSvgFile`), or a PNG drawn from it (`svgToPng`) for the destinations
 * that need a bitmap. Nothing but the platform: SVG markup, an `<img>`, a
 * canvas and `toBlob`.
 *
 * An SVG on its own, or drawn as an image, is sealed off from the page: it
 * cannot read the page's stylesheets, custom properties or fonts, and an
 * image cannot load anything by URL. So the markup is made self-contained:
 *
 * - every `var(--token)` is replaced by its value on the page's root;
 * - every `<image>` is fetched and inlined as a data URL, which also keeps
 *   the canvas untainted, so it must be same-origin (or CORS-enabled) or a
 *   data URL already;
 * - each face in `fonts` is fetched and embedded as an `@font-face` data URL.
 *
 * A face not passed in `fonts` is drawn in the fallback its `font-family`
 * names (Georgia, or the system sans-serif), not the brand face.
 */

const SVG_NS = "http://www.w3.org/2000/svg";

export interface EmbeddedFont {
  /** The `font-family` the SVG names, e.g. "Work Sans". */
  family: string;
  /** Where to fetch the face. Same-origin or CORS-enabled. */
  url: string;
  /** A weight or a range, as `@font-face` takes it, e.g. "400" or "400 700". */
  weight?: string;
}

export interface StandaloneSvgOptions {
  /** The PNG's size in pixels. */
  width: number;
  height: number;
  fonts?: EmbeddedFont[];
}

async function toDataUrl(url: string): Promise<string> {
  if (url.startsWith("data:")) return url;
  const response = await fetch(url);
  if (!response.ok) throw new Error(`svgToPng: fetching ${url} failed with ${response.status}`);
  const blob = await response.blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error(`svgToPng: reading ${url} failed`));
    reader.readAsDataURL(blob);
  });
}

/** Each `var(--name)` swapped for the value the page's root gives it. A
 * token the page does not define is an error: drawn, it would be black. */
function resolveCustomProperties(markup: string): string {
  const root = getComputedStyle(document.documentElement);
  return markup.replace(/var\((--[\w-]+)\)/g, (_, name: string) => {
    const value = root.getPropertyValue(name).trim();
    if (!value) throw new Error(`svgToPng: ${name} is not defined on the page`);
    return value;
  });
}

/** The SVG as self-contained markup: tokens resolved, artwork and faces inlined. */
async function standaloneMarkup(svg: SVGSVGElement, { width, height, fonts = [] }: StandaloneSvgOptions): Promise<string> {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute("xmlns", SVG_NS);
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(height));

  await Promise.all(
    Array.from(clone.querySelectorAll("image"), async (image) => {
      const href = image.getAttribute("href");
      if (href) image.setAttribute("href", await toDataUrl(href));
    })
  );

  if (fonts.length) {
    const faces = await Promise.all(
      fonts.map(
        async (f) =>
          `@font-face { font-family: "${f.family}"; src: url("${await toDataUrl(f.url)}");` +
          (f.weight ? ` font-weight: ${f.weight};` : "") +
          " }"
      )
    );
    const style = document.createElementNS(SVG_NS, "style");
    style.textContent = faces.join("\n");
    clone.insertBefore(style, clone.firstChild);
  }

  return resolveCustomProperties(new XMLSerializer().serializeToString(clone));
}

/**
 * The SVG as a file that renders the same anywhere: no reference leaves it,
 * so a raster sky still travels inside it as a data URL.
 */
export async function svgToSvgFile(svg: SVGSVGElement, options: StandaloneSvgOptions): Promise<Blob> {
  return new Blob([await standaloneMarkup(svg, options)], { type: "image/svg+xml" });
}

/** The SVG drawn to a PNG, for destinations that take only a bitmap. */
export async function svgToPng(svg: SVGSVGElement, options: StandaloneSvgOptions): Promise<Blob> {
  const { width, height } = options;
  const markup = await standaloneMarkup(svg, options);
  const image = new Image(width, height);
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
  await image.decode();

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("svgToPng: no 2D canvas context");
  context.drawImage(image, 0, 0, width, height);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("svgToPng: the canvas gave no PNG"))), "image/png");
  });
}
